import * as THREE from 'three';

interface Tracer {
  mesh: THREE.Mesh;
  ttlMs: number;
  lifeMs: number;
}

interface Impact {
  mesh: THREE.Mesh;
  ttlMs: number;
  lifeMs: number;
}

const TRACER_LIFE_MS = 90;
const IMPACT_LIFE_MS = 400;
const POOL_SIZE = 64;

/**
 * Pooled hit-scan feedback: tracers, impact marks and muzzle flash. Everything
 * is preallocated — allocating meshes during a firefight is a reliable way to
 * turn a GC pause into a missed shot.
 */
export class EffectsRenderer {
  private readonly tracers: Tracer[] = [];
  private readonly impacts: Impact[] = [];
  private readonly muzzleFlash: THREE.PointLight;
  private muzzleTtlMs = 0;

  constructor(private readonly scene: THREE.Scene) {
    const tracerGeometry = new THREE.CylinderGeometry(0.012, 0.012, 1, 5, 1, true);
    // Cylinders are Y-up; rotating the geometry once lets us aim with lookAt.
    tracerGeometry.rotateX(Math.PI / 2);
    tracerGeometry.translate(0, 0, 0.5);

    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(
        tracerGeometry,
        new THREE.MeshBasicMaterial({
          color: 0xffe9a8,
          transparent: true,
          depthWrite: false,
        }),
      );
      mesh.visible = false;
      mesh.frustumCulled = false;
      scene.add(mesh);
      this.tracers.push({ mesh, ttlMs: 0, lifeMs: TRACER_LIFE_MS });
    }

    const impactGeometry = new THREE.CircleGeometry(0.07, 8);
    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(
        impactGeometry,
        new THREE.MeshBasicMaterial({
          color: 0x101218,
          transparent: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -2,
        }),
      );
      mesh.visible = false;
      scene.add(mesh);
      this.impacts.push({ mesh, ttlMs: 0, lifeMs: IMPACT_LIFE_MS });
    }

    this.muzzleFlash = new THREE.PointLight(0xffd08a, 0, 6, 2);
    scene.add(this.muzzleFlash);
  }

  spawnTracer(
    origin: THREE.Vector3 | { x: number; y: number; z: number },
    end: THREE.Vector3 | { x: number; y: number; z: number },
  ): void {
    const tracer = this.tracers.find((entry) => entry.ttlMs <= 0) ?? this.tracers[0]!;
    const mesh = tracer.mesh;
    mesh.position.set(origin.x, origin.y, origin.z);
    mesh.lookAt(end.x, end.y, end.z);
    const length = Math.hypot(end.x - origin.x, end.y - origin.y, end.z - origin.z);
    mesh.scale.set(1, 1, Math.max(0.01, length));
    mesh.visible = true;
    tracer.ttlMs = TRACER_LIFE_MS;
  }

  spawnImpact(point: { x: number; y: number; z: number }): void {
    const impact = this.impacts.find((entry) => entry.ttlMs <= 0) ?? this.impacts[0]!;
    impact.mesh.position.set(point.x, point.y, point.z);
    // Face the camera-ish; a proper decal needs the surface normal from the trace.
    impact.mesh.lookAt(point.x, point.y + 1, point.z + 0.001);
    impact.mesh.visible = true;
    impact.ttlMs = IMPACT_LIFE_MS;
  }

  flashMuzzle(position: THREE.Vector3): void {
    this.muzzleFlash.position.copy(position);
    this.muzzleFlash.intensity = 8;
    this.muzzleTtlMs = 45;
  }

  update(dtMs: number): void {
    for (const tracer of this.tracers) {
      if (tracer.ttlMs <= 0) continue;
      tracer.ttlMs -= dtMs;
      if (tracer.ttlMs <= 0) {
        tracer.mesh.visible = false;
        continue;
      }
      (tracer.mesh.material as THREE.MeshBasicMaterial).opacity =
        tracer.ttlMs / tracer.lifeMs;
    }

    for (const impact of this.impacts) {
      if (impact.ttlMs <= 0) continue;
      impact.ttlMs -= dtMs;
      if (impact.ttlMs <= 0) {
        impact.mesh.visible = false;
        continue;
      }
      (impact.mesh.material as THREE.MeshBasicMaterial).opacity = Math.min(
        1,
        impact.ttlMs / impact.lifeMs,
      );
    }

    if (this.muzzleTtlMs > 0) {
      this.muzzleTtlMs -= dtMs;
      this.muzzleFlash.intensity = Math.max(0, (this.muzzleTtlMs / 45) * 8);
    }
  }

  dispose(): void {
    for (const tracer of this.tracers) {
      this.scene.remove(tracer.mesh);
      (tracer.mesh.material as THREE.Material).dispose();
    }
    for (const impact of this.impacts) {
      this.scene.remove(impact.mesh);
      (impact.mesh.material as THREE.Material).dispose();
    }
    this.tracers[0]?.mesh.geometry.dispose();
    this.impacts[0]?.mesh.geometry.dispose();
    this.scene.remove(this.muzzleFlash);
  }
}
